import { byFieldName } from '@/util/sorting'

export type DomainValue = 'enterprise-attack' | 'ics-attack'
export type TechniqueFilterProps = {
  appliesToPlatforms?: string[] | undefined
  // if undefined show all
  isRevoked?: boolean | undefined
  // if undefined show all
  isDeprecated?: boolean | undefined
}

export type MitreType =
  | 'x-mitre-collection' // remove
  | 'attack-pattern'
  | 'campaign' // remove
  | 'course-of-action' // remove
  | 'identity' // remove
  | 'intrusion-set' // remove
  | 'malware' // remove
  | 'marking-definition' // remove
  | 'relationship' // KEEP
  | 'tool' // remove
  | 'x-mitre-data-component' // remove ?
  | 'x-mitre-data-source' // remove ?
  | 'x-mitre-matrix' // KEEP
  | 'x-mitre-tactic' // KEEP

export interface MitreEntry {
  [key: string]: any

  type: MitreType
}

export interface MitreMatrix extends MitreEntry {
  type: 'x-mitre-matrix'
  tactic_refs: string[]
}

export interface Relationship extends MitreEntry {
  source_ref: string
  target_ref: string
}

export interface Tactic extends MitreEntry {
  techniques?: Technique[]
  type: 'x-mitre-tactic'
}

export interface Technique extends MitreEntry {
  x_mitre_platforms: string[]
  x_mitre_domains: DomainValue[]
  // Can be removed
  x_mitre_contributors: string[]
  // Can be removed
  // 'attack-pattern--...' style ids
  id: string
  // TP0000.000 style ids
  mitre_id: string
  type: 'attack-pattern'
  // RFC3339
  revoked: boolean
  external_references: {
    source_name: string
    description: string
    external_id?: string
    url: string
  }[]
  name: string
  // markdown
  description: string
  kill_chain_phases: {
    kill_chain_name: string
    phase_name: string
  }[]
  // markdown
  spec_version: string
  x_mitre_deprecated: boolean
  x_mitre_detection: string
  x_mitre_is_subtechnique: boolean
  x_mitre_modified_by_ref: string
  x_mitre_shortname?: string
}

export type MitreObject = MitreMatrix | Tactic | Technique | Relationship

export interface DomainInFile {
  domain: string
  objects: Array<MitreObject>
  platforms: string[]
}

export type EntityTypeString = 'relationship' | 'tactic' | 'technique'
export type EntityType = Relationship | Tactic | Technique

export const defaultTechniqueFilterProps: TechniqueFilterProps = {
  appliesToPlatforms: undefined,
  isRevoked: false,
  isDeprecated: false,
}

/**
 * Represents a Matrix (collection of tactics/techniques) for a Mitre Domain.
 * In practice, this could be a union of all domains (i.e. Enterprise AND ICS).
 * This provides a collection of functions and attributes to work with the Matrix
 * in an application setting.
 * @constructor
 * @param {DomainInFile} domainObj - The domain object.
 */
export class MatrixDomain {
  readonly domain: string
  readonly objects: MitreObject[]
  readonly matrix: MitreMatrix | undefined
  readonly platforms: string[]
  readonly relationships: Relationship[]
  readonly tactics: Tactic[]
  readonly techniques: Technique[]

  constructor(domainObj: DomainInFile) {
    this.domain = domainObj.domain
    this.objects = domainObj.objects
    // @ts-ignore
    this.matrix = this.objects.find(
      (o: MitreObject) => o.type === 'x-mitre-matrix',
    )
    this.platforms = domainObj.platforms
    // @ts-ignore
    this.relationships = this.objects.filter(
      (o: MitreObject) => o.type === 'relationship',
    )
    this.techniques = this.objects.filter(
      // @ts-ignore
      (o: Technique) =>
        o.type === 'attack-pattern' && !o.x_mitre_deprecated && !o.revoked,
    )
    // requires this.techniques to be set
    this.tactics = this.objects
      .filter((o: MitreObject) => o.type == 'x-mitre-tactic')
      // @ts-ignore
      .map((tactic: Tactic) => this.assignTechniquesToTactic(tactic))
  }

  /**
   * Retrieves an array of objects for a given entity type.
   *
   * @param {string} entity - The entity type for which to retrieve objects.
   *                          Acceptable values are 'relationship', 'tactic', and 'technique'.
   *
   * @return {Array} - An array of objects for the specified entity type.
   *
   * @throws {Error} - If the entity type is not one of the acceptable values.
   */
  _getEntity(entity: EntityTypeString): EntityType[] {
    let objs
    if (entity === 'relationship') {
      objs = this.relationships
    } else if (entity === 'tactic') {
      objs = this.tactics
    } else if (entity === 'technique') {
      objs = this.techniques
    } else {
      throw new Error('No objs found for entity type:' + entity)
    }
    return objs
  }

  /**
   * Attaches Mitre techniques to a given Mitre tactic.
   *
   * @param {Tactic} tactic - The tactic to assign techniques to.
   * @return {Tactic} - The updated tactic object with assigned techniques.
   */
  assignTechniquesToTactic(tactic: Tactic): Tactic {
    const techniques = this.getTechniquesForTactic(tactic.x_mitre_shortname)
    return { ...tactic, techniques }
  }

  /**
   * Returns a list of entities based on the specified type ("relationship" | "tactic" | "technique")
   * that can be queried via chained methods.
   *
   * @param {string} entity - The entity type string.
   * @return {object} - An object with methods for querying the list of entities.
   *     - `all()`: Returns all entities in the list.
   *     - `where(fieldName)`: Returns an object with methods for performing
   *       field-based queries on the list of entities.
   *         - `equals(val)`: Returns entities with a field value equal to the specified value.
   *         - `includes(val)`: Returns entities with a field value that includes the specified value.
   */
  getListOf(entity: EntityTypeString) {
    const objs = this._getEntity(entity)
    return {
      all: () => objs,
      where: (fieldName: string) => ({
        equals: (val: any): EntityType[] =>
          objs.filter((o) => o[fieldName] === val),
        includes: (val: any): EntityType[] =>
          objs.filter((o) => o[fieldName]?.includes(val)),
      }),
    }
  }

  /**
   * Retrieves a single entity from a list of entities based on a specific field value.
   *
   * @param {string} entity - The type of entity to retrieve.
   * @return {Object} - An object with a `where` method that allows filtering entities based on field values.
   *     The `where` method returns an object with an `equals` method which can be used to find an entity
   *     that matches the specified field value.
   */
  getSingle(entity: EntityTypeString) {
    const objs = this._getEntity(entity)
    return {
      where: (fieldName: string) => ({
        equals: (val: any): EntityType | undefined =>
          objs.find((o) => o[fieldName] === val),
      }),
    }
  }

  /**
   * Retrieves techniques for a given Mitre tactic.
   *
   * @param {string} tactic_x_mitre_shortname - The shortname of the tactic.
   * @return {Technique[]} - An array of techniques associated with the given tactic.
   */
  getTechniquesForTactic(tactic_x_mitre_shortname: string): Technique[] {
    return (
      this.techniques
        .filter(
          (c: { [key: string]: any }) =>
            c.kill_chain_phases
              .map((kcp: { [key: string]: any }) => kcp.phase_name)
              .includes(tactic_x_mitre_shortname) && !c.x_mitre_is_subtechnique,
        )
        // @ts-ignore
        .filter(this.constructor.techniqueFilter({}))
        .map((technique: Technique) => ({
          ...technique,
          subtechniques: this.getSubTechniques(technique.id),
        }))
        .sort(byFieldName('name', true))
    )
  }

  /**
   * Retrieves the IDs of sub-techniques related to the given Mitre technique's ID.
   *
   * @param {string} techniqueId - The ID of the technique to find sub-technique IDs for.
   * @return {Array} - An array of sub-technique IDs.
   */
  getSubTechniqueIds(techniqueId: string): string[] {
    return (
      this.getListOf('relationship')
        .where('target_ref')
        .equals(techniqueId)
        // @ts-ignore
        .map((r: Relationship) => r.source_ref)
    )
  }

  /**
   * Retrieves the sub techniques of a given Mitre technique.
   *
   * @param {string} techniqueId - The identifier of the technique.
   *
   * @returns {Technique[]} - An array of sub techniques.
   */
  getSubTechniques(techniqueId: string): Technique[] {
    const ids = this.getSubTechniqueIds(techniqueId)
    // @ts-ignore
    return (
      this.getListOf('technique')
        .all()
        // @ts-ignore
        .filter((c: Technique) => ids.includes(c.id))
        // @ts-ignore
        .filter(this.constructor.techniqueFilter({}))
        .sort(byFieldName('name', true))
    )
  }

  /**
   * Determines if a Mitre technique has defined sub-techniques.
   *
   * @param {Technique} technique - The technique to check.
   * @return {boolean} - True if the technique has sub-techniques, otherwise false.
   */
  techniqueHasSubTechniques(technique: Technique) {
    return (
      !technique.x_mitre_is_subtechnique &&
      this.getSubTechniqueIds(technique.id).length > 0
    )
  }

  /**
   * Filters an array of Mitre techniques based on the provided filter properties.
   *
   * @param {TechniqueFilterProps} filterProps - The filter properties to apply when filtering techniques.
   * @returns {function(Technique): boolean} - A function that takes a Technique object and returns a boolean
   * indicating whether the technique passes the filter or not.
   */
  static techniqueFilter(
    filterProps: TechniqueFilterProps = {},
  ): (technique: Technique) => boolean {
    filterProps = { ...defaultTechniqueFilterProps, ...filterProps }

    return (technique: Technique) => {
      return [
        typeof filterProps.appliesToPlatforms === 'undefined' ||
          technique.x_mitre_platforms.some((platform) =>
            filterProps.appliesToPlatforms?.includes(platform),
          ),
      ].every((c) => c)
    }
  }
}
